import { useCallback, useEffect, useRef, useState } from 'react';

import { platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';

import { isPasskeysEnabled } from '@/constant/features';
import { DISCLAIMER_FLAG_KEY } from '@/constant/localStorage';
import {
  DISCLAIMER_ACCEPTED_EVENT,
  PASSKEY_ENROL_MODAL_ID,
} from '@/constant/modals';
import { useModal } from '@/hooks/useModal';
import { useUser } from '@/store/auth';
import { usePasskeys } from '@/store/passkeys';
import { glassyEffectModal } from '@/styles/variants/glassy';
import storage from '@/utils/storage';

import { PasskeyEnrolPrompt } from './PasskeyEnrolPrompt';
import {
  clearPasswordSignIn,
  dismissPrompt,
  isPasswordSignInPending,
  isPromptDismissed,
} from './storage';

/**
 * Offers a passkey once, after a password sign-in in this tab, when the device has a built-in
 * authenticator, the User has no passkeys, and they haven't said "Not now" on this device.
 * On a device's first sign-in it waits for the AI disclaimer, so the two dialogs never stack.
 */
export function usePasskeyEnrolPrompt() {
  const { user } = useUser();
  const pending = useRef(isPasskeysEnabled && isPasswordSignInPending());
  const { data: passkeys } = usePasskeys();
  const { openModal, closeModal } = useModal();
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(
    () => !!storage.getItem(DISCLAIMER_FLAG_KEY)
  );

  useEffect(() => {
    const onAccepted = () => setDisclaimerAccepted(true);
    window.addEventListener(DISCLAIMER_ACCEPTED_EVENT, onAccepted);
    return () =>
      window.removeEventListener(DISCLAIMER_ACCEPTED_EVENT, onAccepted);
  }, []);

  const finish = useCallback(() => {
    pending.current = false;
    clearPasswordSignIn();
  }, []);

  useEffect(() => {
    if (!pending.current || !user || !disclaimerAccepted || !passkeys) return;
    if (passkeys.length > 0 || isPromptDismissed(user.id)) {
      finish();
      return;
    }
    let cancelled = false;
    void platformAuthenticatorIsAvailable().then(available => {
      if (cancelled || !pending.current) return;
      finish();
      if (!available) return;
      openModal({
        id: PASSKEY_ENROL_MODAL_ID,
        body: (
          <PasskeyEnrolPrompt
            onDone={() => closeModal(PASSKEY_ENROL_MODAL_ID)}
            onDismiss={() => {
              dismissPrompt(user.id);
              closeModal(PASSKEY_ENROL_MODAL_ID);
            }}
          />
        ),
        size: 'md',
        hideCloseButton: true,
        hideCustomCloseButton: true,
        isCentered: true,
        closeOnEsc: false,
        closeOnOverlayClick: false,
        headerProps: { display: 'none' },
        contentProps: {
          maxW: 'min(480px, calc(100vw - 16px))',
          rounded: '16px',
          overflow: 'hidden',
          sx: { ...glassyEffectModal, '& > div': { p: 0 } },
        },
        overlayProps: { bg: 'blackAlpha.500' },
      });
    });
    return () => {
      cancelled = true;
    };
  }, [user, disclaimerAccepted, passkeys, finish, openModal, closeModal]);
}
